<script setup lang="ts">
/**
 * Rendu des champs personnalisés dans le formulaire participant
 * Utilisé sur la page de paiement /e/[slug]/payment.vue
 *
 * Question « Fichier » (Karim 2026-09-29) : le document part dès qu'il est
 * choisi, vers `urlEnvoiDocument` ; la réponse devient `{ file_id, file_name }`.
 */
import { useErreursEffaceesALaSaisie } from "~/composables/useErreursEffaceesALaSaisie";
import {
  ACCEPT_DOCUMENT,
  envoyerDocumentFormulaire,
  estDocumentEnvoye,
  type DocumentEnvoye,
} from "~/composables/useEnvoiDocumentFormulaire";
import type { EventCustomFieldDef } from "~/types/event";
import { CalendarDate } from "@internationalized/date";
import { exempleTelephone } from "@donoor/utils";
import { usePaysAssociation } from "~/composables/usePaysAssociation";
import {
  BORDURE_CHAMP_PUBLIC,
  BORDURE_CHAMP_PUBLIC_ERREUR,
  UI_CHAMP_PUBLIC,
  UI_CHAMP_PUBLIC_ERREUR,
} from "~/utils/stylesChamps";

const { t } = useI18n();
const { localeIntl } = useFormatLangue();

// Mois de l'année dans la langue de la page (Intl), initiale en capitale —
// en français « Janvier »… « Décembre », identiques à l'ancienne liste.
const moisCapitalises = computed(() =>
  Array.from({ length: 12 }, (_, i) => {
    const nom = new Intl.DateTimeFormat(localeIntl.value, { month: "long" }).format(new Date(2000, i, 1));
    return nom.charAt(0).toUpperCase() + nom.slice(1);
  }),
);

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();

const props = defineProps<{
  fields: EventCustomFieldDef[];
  /** Erreurs de validation par CLÉ de champ. Vide = aucune erreur. */
  erreurs?: Record<string, string>;
  /** Préfixe des `id` HTML — distinct par participant (ids sinon dupliqués). */
  prefixeId?: string;
  /**
   * Adresse d'envoi des documents (question « Fichier ») :
   * `…/api/public/events/:slug/documents` ou `…/shops/:slug/documents`.
   */
  urlEnvoiDocument?: string;
}>();

/** Id HTML stable — cible du défilement au premier champ fautif. */
function idChamp(cle: string): string {
  return `${props.prefixeId ?? "champ"}-${cle}`;
}


/**
 * Téléphone : chiffres et séparateurs SEULEMENT (Karim 2026-09-21).
 * `type="tel"` ne filtre rien — il change juste le clavier mobile.
 */
function filtrerTelephone(cle: string, valeur: string | number) {
  const propre = String(valeur ?? "").replace(/[^\d\s+().-]/g, "");
  values.value[cle] = propre;
}

// Reason: champs personnalisés event = types hétérogènes (string/number/bool/array/date)
// déterminés dynamiquement par EventCustomFieldDef.type. UInput/textarea acceptent un v-model
// type-permissive; restreindre causerait des incompatibilités multi-types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const values = defineModel<Record<string, any>>({ default: () => ({}) });

// Une erreur s'efface dès qu'on modifie le champ (cf. composable).
const { erreurDe } = useErreursEffaceesALaSaisie(
  () => props.erreurs,
  () => ({ ...values.value }),
);

/** Réponses retenues d'un choix multiple (une ancienne réponse unique compte). */
function reponsesDe(cle: string): string[] {
  const v = values.value[cle];
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  return typeof v === "string" && v !== "" ? [v] : [];
}

/** Coche / décoche une option, en gardant l'ordre des options. */
function basculerOption(field: EventCustomFieldDef, option: string) {
  const avant = reponsesDe(field.key);
  const apres = avant.includes(option) ? avant.filter((o) => o !== option) : [...avant, option];
  const ordre = field.options ?? [];
  values.value[field.key] = ordre.filter((o, i) => apres.includes(o) && ordre.indexOf(o) === i);
}

// Date picker state (per field key)
const datePopoverOpen = ref<Record<string, boolean>>({});
const datePlaceholder = ref<Record<string, CalendarDate>>({});

// Year options for date picker (from 1920 to current year)
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 1919 }, (_, i) => currentYear - i);

function onYearChange(fieldKey: string, year: number) {
  const current = datePlaceholder.value[fieldKey] || new CalendarDate(currentYear, new Date().getMonth() + 1, 1);
  datePlaceholder.value[fieldKey] = new CalendarDate(year, current.month, 1);
}

function onMonthChange(fieldKey: string, month: number) {
  const current = datePlaceholder.value[fieldKey] || new CalendarDate(currentYear, new Date().getMonth() + 1, 1);
  datePlaceholder.value[fieldKey] = new CalendarDate(current.year, month, 1);
}

function getCalendarDate(fieldKey: string): CalendarDate | undefined {
  const val = values.value[fieldKey];
  if (!val || typeof val !== "string") return undefined;
  const d = new Date(val);
  if (isNaN(d.getTime())) return undefined;
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function setCalendarDate(fieldKey: string, val: CalendarDate | undefined) {
  if (!val) return;
  // Store as YYYY-MM-DD
  values.value[fieldKey] = `${val.year}-${String(val.month).padStart(2, "0")}-${String(val.day).padStart(2, "0")}`;
  datePopoverOpen.value[fieldKey] = false;
}

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(localeIntl.value, { day: "numeric", month: "long", year: "numeric" });
}

// ─── Question « Fichier » ─────────────────────────────────────────────
const envoisEnCours = ref<Record<string, boolean>>({});
const erreursEnvoi = ref<Record<string, string>>({});

/** Le document déjà envoyé pour cette question, s'il y en a un. */
function documentDe(cle: string): DocumentEnvoye | null {
  const valeur = values.value[cle];
  return estDocumentEnvoye(valeur) ? valeur : null;
}

async function choisirDocument(cle: string, evenement: Event) {
  const champ = evenement.target as HTMLInputElement;
  const fichier = champ.files?.[0];
  // Vidé tout de suite : re-choisir le même fichier après une erreur relance l'envoi.
  champ.value = "";
  if (!fichier) return;
  if (!props.urlEnvoiDocument) {
    erreursEnvoi.value = { ...erreursEnvoi.value, [cle]: t("association.champsFormulaire.envoiIndisponible") };
    return;
  }
  erreursEnvoi.value = { ...erreursEnvoi.value, [cle]: "" };
  envoisEnCours.value = { ...envoisEnCours.value, [cle]: true };
  try {
    values.value[cle] = await envoyerDocumentFormulaire(props.urlEnvoiDocument, fichier);
  } catch (erreur) {
    erreursEnvoi.value = {
      ...erreursEnvoi.value,
      [cle]: erreur instanceof Error ? erreur.message : t("association.champsFormulaire.envoiEchec"),
    };
  } finally {
    envoisEnCours.value = { ...envoisEnCours.value, [cle]: false };
  }
}

function retirerDocument(cle: string) {
  values.value[cle] = undefined;
}

// Initialize values for boolean fields
onMounted(() => {
  for (const field of props.fields) {
    if (field.type === "boolean" && values.value[field.key] === undefined) {
      values.value[field.key] = false;
    }
  }
});
</script>

<template>
  <div class="space-y-4">
    <div v-for="field in fields" :key="field.key">
      <!-- Label -->
      <label class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">
        {{ field.label }}
        <span v-if="field.required" class="text-red-500">*</span>
      </label>

      <!-- Text -->
      <UInput
        v-if="field.type === 'text'"
        v-model="values[field.key]"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        size="lg"
        :ui="{ base: erreurDe(field.key) ? UI_CHAMP_PUBLIC_ERREUR : UI_CHAMP_PUBLIC }"
      />

      <!-- Textarea -->
      <textarea
        v-else-if="field.type === 'textarea'"
        v-model="values[field.key]"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        rows="3"
        class="w-full rounded-xl bg-white dark:bg-[#141414] px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 transition-colors resize-none"
        :class="erreurDe(field.key) ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC"
      />

      <!-- Number -->
      <UInput
        v-else-if="field.type === 'number'"
        v-model.number="values[field.key]"
        type="number"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        size="lg"
        :ui="{ base: erreurDe(field.key) ? UI_CHAMP_PUBLIC_ERREUR : UI_CHAMP_PUBLIC }"
      />

      <!-- Email -->
      <UInput
        v-else-if="field.type === 'email'"
        v-model="values[field.key]"
        type="email"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        size="lg"
        :ui="{ base: erreurDe(field.key) ? UI_CHAMP_PUBLIC_ERREUR : UI_CHAMP_PUBLIC }"
      />

      <!-- Phone -->
      <UInput
        v-else-if="field.type === 'phone'"
        :id="idChamp(field.key)"
        :model-value="values[field.key]"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        :placeholder="field.placeholder || exempleTelephone(paysAsso)"
        :required="field.required"
        size="lg"
        :ui="{ base: erreurDe(field.key) ? UI_CHAMP_PUBLIC_ERREUR : UI_CHAMP_PUBLIC }"
        @update:model-value="filtrerTelephone(field.key, $event)"
      />

      <!-- Adresse : autocomplétion Base Adresse Nationale, comme le dashboard. -->
      <FieldAddressInput
        v-else-if="field.type === 'address'"
        :id="idChamp(field.key)"
        v-model="values[field.key]"
        :placeholder="field.placeholder || undefined"
        :en-erreur="!!erreurDe(field.key)"
      />

      <!-- Date -->
      <UPopover v-else-if="field.type === 'date'" v-model:open="datePopoverOpen[field.key]" :ui="{ content: 'rounded-2xl! dark:bg-[#141414]!' }">
        <button
          type="button"
          class="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-white dark:bg-[#141414] transition-all duration-200"
          :class="erreurDe(field.key) ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC"
        >
          <span :class="values[field.key] ? 'text-gray-900 dark:text-gray-100 text-sm' : 'text-gray-400 dark:text-gray-500 text-sm'">
            {{ values[field.key] ? formatDateDisplay(values[field.key]) : (field.placeholder || $t("association.champsFormulaire.selectionnerDate")) }}
          </span>
          <div class="flex items-center gap-1.5">
            <button v-if="values[field.key]" type="button" class="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors" @click.stop="values[field.key] = ''">
              <UIcon name="i-lucide-x" class="size-3.5 text-gray-400" />
            </button>
            <UIcon name="i-lucide-calendar" class="size-4 text-gray-400" />
          </div>
        </button>
        <template #content>
          <div class="p-3">
            <!-- Month/Year selectors -->
            <div class="flex items-center gap-2 mb-3">
              <select
                class="flex-1 px-2 py-1.5 text-sm font-medium bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none"
                :value="(datePlaceholder[field.key] || getCalendarDate(field.key))?.month || new Date().getMonth() + 1"
                @change="onMonthChange(field.key, Number(($event.target as HTMLSelectElement).value))"
              >
                <option v-for="(m, idx) in moisCapitalises" :key="idx" :value="idx + 1">{{ m }}</option>
              </select>
              <select
                class="w-24 px-2 py-1.5 text-sm font-medium bg-white dark:bg-[#141414] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none"
                :value="(datePlaceholder[field.key] || getCalendarDate(field.key))?.year || currentYear"
                @change="onYearChange(field.key, Number(($event.target as HTMLSelectElement).value))"
              >
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <!-- ⚠️ NE PAS remettre `disable-days-outside-current-view` :
                 le clic sur une date du mois adjacent doit auto-naviguer
                 vers ce mois et sélectionner la date. Cf. dashboard
                 StepDateLocation.vue commentaire ligne ~194. -->
            <UCalendar
              :model-value="(getCalendarDate(field.key) ?? null) as any"
              :placeholder="(datePlaceholder[field.key] ?? undefined) as any"
              color="primary"
              class="rounded-xl"
              :fixed-weeks="false"
              :month-controls="false"
              :year-controls="false"
              @update:model-value="(val: CalendarDate | unknown) => setCalendarDate(field.key, val instanceof CalendarDate ? val : undefined)"
              @update:placeholder="(val: CalendarDate | unknown) => { if (val instanceof CalendarDate) datePlaceholder[field.key] = val; }"
            />
          </div>
        </template>
      </UPopover>

      <!-- Boolean (Yes/No) -->
      <div v-else-if="field.type === 'boolean'" class="flex items-center gap-3">
        <button
          v-for="opt in [{ label: 'Oui', val: true }, { label: 'Non', val: false }]"
          :key="String(opt.val)"
          type="button"
          class="flex-1 py-2.5 px-4 rounded-xl border text-sm font-medium transition-colors text-center"
          :class="values[field.key] === opt.val
            ? 'border-main2 bg-main2/5 dark:bg-main2/10 text-main2'
            : 'border-gray-200 dark:border-[#262626] text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'"
          @click="values[field.key] = values[field.key] === opt.val ? undefined : opt.val"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- Choix MULTIPLE : cases carrées, plusieurs réponses possibles. -->
      <div
        v-else-if="field.type === 'select' && field.multiple && field.options"
        :id="idChamp(field.key)"
        tabindex="-1"
        role="group"
        :aria-label="field.label"
        class="space-y-2 focus:outline-none"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ $t("association.champsFormulaire.plusieursReponses") }}</p>
        <button
          v-for="option in [...new Set(field.options)]"
          :key="option"
          type="button"
          role="checkbox"
          :aria-checked="reponsesDe(field.key).includes(option)"
          class="w-full flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors text-left"
          :class="reponsesDe(field.key).includes(option)
            ? 'border-main2 bg-main2/5 dark:bg-main2/10'
            : erreurDe(field.key)
              ? 'border-red-500 dark:border-red-500 hover:border-red-600'
              : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
          @click="basculerOption(field, option)"
        >
          <div
            class="w-4 h-4 rounded-[4px] border-2 shrink-0 flex items-center justify-center transition-colors"
            :class="reponsesDe(field.key).includes(option) ? 'border-main2 bg-main2' : 'border-gray-300 dark:border-gray-600'"
          >
            <UIcon v-if="reponsesDe(field.key).includes(option)" name="i-lucide-check" class="size-3 text-white" />
          </div>
          <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
        </button>
      </div>

      <!-- Select -->
      <div v-else-if="field.type === 'select' && field.options" class="space-y-2">
        <button
          v-for="option in field.options"
          :key="option"
          type="button"
          class="w-full flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors text-left"
          :class="values[field.key] === option
            ? 'border-main2 bg-main2/5 dark:bg-main2/10'
            : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
          @click="values[field.key] = values[field.key] === option ? undefined : option"
        >
          <div
class="w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors"
            :class="values[field.key] === option ? 'border-main2' : 'border-gray-300 dark:border-gray-600'">
            <div v-if="values[field.key] === option" class="w-2 h-2 rounded-full bg-main2" />
          </div>
          <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
        </button>
      </div>

      <!-- Fichier (document) : envoyé dès qu'il est choisi. -->
      <div
        v-else-if="field.type === 'file'"
        :id="idChamp(field.key)"
        tabindex="-1"
        class="focus:outline-none"
        data-testid="champ-document"
      >
        <div
          v-if="envoisEnCours[field.key]"
          class="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-gray-50 dark:bg-[#1a1a1a] flex items-center gap-3"
        >
          <UIcon name="i-lucide-loader-2" class="size-4 text-gray-400 animate-spin" />
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t("association.champsFormulaire.envoiDocument") }}</span>
        </div>
        <div
          v-else-if="documentDe(field.key)"
          class="w-full px-4 py-3 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 flex items-center justify-between gap-3"
          data-testid="document-envoye"
        >
          <div class="flex items-center gap-2 min-w-0">
            <UIcon name="i-lucide-check-circle" class="size-4 text-green-600 dark:text-green-400 shrink-0" />
            <span class="text-sm text-green-800 dark:text-green-300 truncate">{{ documentDe(field.key)?.file_name }}</span>
          </div>
          <button
            type="button"
            class="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0 font-medium"
            @click="retirerDocument(field.key)"
          >
            {{ $t("association.champsFormulaire.retirer") }}
          </button>
        </div>
        <label
          v-else
          class="w-full px-4 py-3 rounded-xl border bg-white dark:bg-[#0f0f0f] flex items-center gap-3 cursor-pointer transition-colors"
          :class="erreurDe(field.key) || erreursEnvoi[field.key]
            ? 'border-red-500 dark:border-red-500'
            : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600 focus-within:border-[#212121] dark:focus-within:border-white'"
        >
          <UIcon name="i-lucide-upload" class="size-4 text-gray-400 shrink-0" />
          <span class="text-sm text-gray-600 dark:text-gray-300">{{ $t("association.champsFormulaire.choisirFichier") }}</span>
          <span class="text-xs text-gray-400 dark:text-gray-500 ml-auto text-right">{{ $t("association.champsFormulaire.formatsFichier") }}</span>
          <input
            type="file"
            class="sr-only"
            :accept="ACCEPT_DOCUMENT"
            data-testid="choisir-document"
            @change="choisirDocument(field.key, $event)"
          />
        </label>
        <p
          v-if="erreursEnvoi[field.key]"
          class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5"
          role="alert"
        >
          <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" />
          <span>{{ erreursEnvoi[field.key] }}</span>
        </p>
      </div>

      <!-- Message d'erreur, sous le champ concerné. -->
      <p
        v-if="erreurDe(field.key)"
        :id="`${idChamp(field.key)}-erreur`"
        class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5"
      >
        <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" />
        <span>{{ erreurDe(field.key) }}</span>
      </p>
    </div>
  </div>
</template>
